/**
 * PlanReadyCard — one-time acknowledgment shown on the dashboard right
 * after a caregiver finishes intake ("See my personalized plan").
 * Reflects what they actually entered and offers clear next steps.
 */
import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, MessageCircle, FileText, X, ShieldAlert, Heart } from "lucide-react";

const SAFETY_LABELS = {
    wandering: "wandering / getting lost",
    falls: "falls or balance",
    driving: "driving",
    stove_cooking: "stove / cooking risks",
    medications: "medication mix-ups",
    firearms: "firearms in the home",
    leaving_home: "leaving home unsupervised",
    stranger_calls: "scams / unwanted calls",
};

export default function PlanReadyCard({ intake, onDismiss }) {
    const safety = (intake?.safety_concerns || []).filter((s) => s && s !== "none");
    const burnout = typeof intake?.burnout_score === "number" ? intake.burnout_score : null;
    const stage = intake?.estimated_stage;

    const points = [];
    if (intake?.predx) {
        points.push({ Icon: CheckCircle2, text: "You're in the pre-diagnosis stage — Lane's guidance is focused on getting answers and preparing for the first appointments." });
    } else if (stage) {
        points.push({ Icon: CheckCircle2, text: `Based on your answers, Lane tuned everything to around stage ${stage} — the guidance, resources, and check-ins you'll see reflect that.` });
    }
    if (safety.length > 0) {
        points.push({ Icon: ShieldAlert, text: `You flagged ${safety.map((s) => SAFETY_LABELS[s] || s).join(", ")} — Lane marked ${safety.length > 1 ? "these" : "this"} as safety priorities and will surface what usually helps.` });
    } else {
        points.push({ Icon: ShieldAlert, text: "No safety concerns flagged right now — Lane will keep an eye out as you log day-to-day changes." });
    }
    if (burnout !== null && burnout >= 7) {
        points.push({ Icon: Heart, text: "You told us the load feels heavy right now. That matters — the Wellness room has short resets, and Lane will check in on you too." });
    }

    return (
        <section
            data-testid="plan-ready-card"
            className="mt-6 rounded-3xl border border-[#D5E6E4] p-5 md:p-7 ml-v2-soft-shadow relative"
            style={{ background: "linear-gradient(135deg, #F3F8F5 0%, #FDFBF4 100%)" }}
        >
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss"
                data-testid="plan-ready-dismiss"
                className="absolute top-4 right-4 text-[#6E7A74] hover:text-[#1F2624] p-2 rounded-full hover:bg-white/70 transition-colors"
            >
                <X size={16} strokeWidth={2} />
            </button>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-teal-dark">Your personalized plan</p>
            <h2 className="mt-1 font-heading text-xl md:text-2xl text-[#2D3748]">
                Thank you — Lane set things up from what you shared.
            </h2>
            <ul className="mt-4 space-y-2.5">
                {points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-[#2D3748] leading-relaxed">
                        <p.Icon size={16} strokeWidth={1.8} className="text-teal-dark mt-0.5 shrink-0" />
                        <span>{p.text}</span>
                    </li>
                ))}
            </ul>
            <p className="mt-4 text-sm text-[#4C5551]">Here&apos;s where to start:</p>
            <div className="mt-2.5 flex flex-wrap items-center gap-3">
                <Link
                    to="/chat"
                    data-testid="plan-ready-talk-lane"
                    className="inline-flex items-center gap-2 rounded-2xl bg-[#12776F] text-[#FDFBF7] px-5 py-2.5 text-sm font-semibold hover:bg-[#0F615A] transition-colors duration-300"
                >
                    <MessageCircle size={14} strokeWidth={1.7} /> Talk through today with Lane
                </Link>
                <Link
                    to="/care-summary"
                    data-testid="plan-ready-care-summary"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4F7F95] hover:text-[#12776F] min-h-[44px] transition-colors duration-300"
                >
                    <FileText size={14} strokeWidth={1.7} /> See your Care Summary
                </Link>
            </div>
        </section>
    );
}
